import { connect } from "react-redux";
import {
  CommonSectionContentComponent as Component,
  CommonSectionContentProps,
  MenuItem,
} from "./CommonSectionContentComponent";

export interface ReduxState {
  cscq: Record<string, { disabled?: string[] }> | undefined;
  mscq: Record<string, { disabled?: string[] }> | undefined;
  oeq: Record<string, { complete?: boolean }> | undefined;
  isq:
    | Record<string, { correctness?: Array<Record<string, boolean>> }>
    | undefined;
  notebook: {
    displayProgress?: boolean;
    usingNewDesignSystem?: boolean;
    generatedVariables?: Record<string, unknown>;
  };
}

interface SectionDependency {
  sectionIndex?: number;
  sectionNumber?: number;
  sectionValue?: string;
  randVarName?: string;
  randVarValue?: unknown;
  componentId?: string;
  answerId?: string | string[];
  any?: boolean;
}

interface DependencyRule {
  dependsOn: SectionDependency[];
  toggleProps?: string[];
  negativeAlignment?: boolean;
  dependencyLogic?: "AND" | "OR";
}

interface MenuItemWithDependencies extends MenuItem {
  locked?: boolean;
  dependencies?: DependencyRule[];
  totalOverride?: number;
}

const isDependencySatisfied = (
  dep: SectionDependency,
  stores: {
    cscq: ReduxState["cscq"];
    mscq: ReduxState["mscq"];
    oeq: ReduxState["oeq"];
    generatedVariables: ReduxState["notebook"]["generatedVariables"];
  },
  menuList: MenuItemWithDependencies[]
): boolean => {
  // Notebook random variable dependency: { randVarName, randVarValue }
  if (typeof dep?.randVarName === "string") {
    const gv = stores.generatedVariables?.[dep.randVarName];
    if (Object.prototype.hasOwnProperty.call(dep, "randVarValue")) {
      return gv == dep.randVarValue;
    }
    return gv !== undefined;
  }

  // Section completion dependency
  if (
    typeof dep?.sectionIndex === "number" ||
    typeof dep?.sectionNumber === "number" ||
    typeof dep?.sectionValue === "string"
  ) {
    let section: MenuItemWithDependencies | null = null;
    if (typeof dep.sectionIndex === "number") {
      section = menuList?.[dep.sectionIndex] ?? null;
    }
    if (!section && typeof dep.sectionNumber === "number") {
      section = menuList?.find((s) => s?.number === dep.sectionNumber) ?? null;
    }
    if (!section && typeof dep.sectionValue === "string") {
      const want = dep.sectionValue.trim();
      section =
        menuList?.find((s) => {
          const t = typeof s?.title === "string" ? s.title.trim() : s?.title;
          const v = typeof s?.value === "string" ? s.value.trim() : s?.value;
          return t === want || v === want;
        }) ?? null;
    }
    if (!section) return false;

    const curr = Number(section.currentProgress ?? 0);
    const max = Number(
      section.totalOverride ??
        section.maxProgress ??
        section.activitiesCount ??
        0
    );

    return max > 0 && curr >= max;
  }

  // Component/answer dependency
  const { componentId, answerId, any } = dep ?? {};
  if (componentId == null) return false;

  const { cscq, mscq, oeq } = stores;

  if (any) {
    const hasCSCQ =
      !!cscq?.[componentId]?.disabled &&
      Array.isArray(cscq[componentId]?.disabled) &&
      (cscq[componentId]?.disabled?.length ?? 0) > 0;
    const hasMSCQ =
      !!mscq?.[componentId]?.disabled &&
      Array.isArray(mscq[componentId]?.disabled) &&
      (mscq[componentId]?.disabled?.length ?? 0) > 0;
    const hasOEQ = !!oeq?.[componentId]?.complete;

    return hasCSCQ || hasMSCQ || hasOEQ;
  } else {
    if (answerId == null) return false;
    const answerIds = Array.isArray(answerId) ? answerId : [answerId];

    const inCSCQ =
      !!cscq?.[componentId]?.disabled &&
      Array.isArray(cscq[componentId]?.disabled) &&
      answerIds.some((id) => cscq[componentId]?.disabled?.includes(id));

    const inMSCQ =
      !!mscq?.[componentId]?.disabled &&
      Array.isArray(mscq[componentId]?.disabled) &&
      answerIds.some((id) => mscq[componentId]?.disabled?.includes(id));
    const inOEQ = !!oeq?.[componentId]?.complete;

    return inCSCQ || inMSCQ || inOEQ;
  }
};

const areDependenciesSatisfied = (
  deps: SectionDependency[],
  stores: {
    cscq: ReduxState["cscq"];
    mscq: ReduxState["mscq"];
    oeq: ReduxState["oeq"];
    generatedVariables: ReduxState["notebook"]["generatedVariables"];
  },
  menuList: MenuItemWithDependencies[],
  negativeAlignment?: boolean,
  dependencyLogic: "AND" | "OR" = "AND"
): boolean => {
  if (!Array.isArray(deps) || deps.length === 0) return false;

  let depsPass: boolean;
  if (dependencyLogic === "OR") {
    depsPass = deps.some((d) => isDependencySatisfied(d, stores, menuList));
  } else {
    depsPass = deps.every((d) => isDependencySatisfied(d, stores, menuList));
  }

  if (negativeAlignment) {
    return !depsPass;
  }
  return depsPass;
};

export function mapStateToProps(
  state: ReduxState,
  ownProps: CommonSectionContentProps
) {
  const { cscq, mscq, oeq, isq, notebook } = state;
  const displayProgress = notebook.displayProgress ?? false;
  const usingNewDesignSystem = notebook.usingNewDesignSystem ?? false;

  let updatedMenuList = [...ownProps.menuList];

  if (ownProps.components && displayProgress) {
    // Process components that are complete, are activities, and are prediction questions
    ownProps.components
      .filter(
        (component) =>
          component.isComplete &&
          component.isActivity &&
          component.isPredictionQuestion
      )
      .forEach((component) => {
        const componentId = component.id;
        let isActivityCompleted = false;

        if (cscq && cscq[componentId] && component.answerId) {
          isActivityCompleted = component.answerId.some((answerId) =>
            Boolean(cscq[componentId]?.disabled?.includes(answerId))
          );
        }

        if (!isActivityCompleted) {
          updatedMenuList = updatedMenuList.map((item) => {
            if (item.value === ownProps.currentMenu.value) {
              if ((item.currentProgress ?? 0) > 0) {
                ownProps.updateProgress({
                  menu: ownProps.currentMenu.value,
                  menuProgress: (item.currentProgress ?? 1) - 1,
                  overallProgress: ownProps.currentProgress - 1,
                });

                // This is a side effect that directly modifies the component passed in,
                // which is not ideal in a pure function but preserved for compatibility
                component.isComplete = false;

                return {
                  ...item,
                  currentProgress: (item.currentProgress ?? 1) - 1,
                };
              }
            }
            return item;
          });
        }
      });

    // Process components that are not complete but are activities
    ownProps.components
      .filter((component) => !component.isComplete && component.isActivity)
      .forEach((component) => {
        const componentId = component.id;
        let isActivityCompleted = false;

        // Check if cscq component is completed
        if (cscq && cscq[componentId] && component.answerId) {
          isActivityCompleted = component.answerId.some((answerId) =>
            Boolean(cscq[componentId]?.disabled?.includes(answerId))
          );
        }

        // Check if mscq component is completed
        if (mscq && mscq[componentId] && component.answerId) {
          const disabledList = mscq[componentId]?.disabled || [];

          // Check if all correct answers are disabled
          const allCorrectDisabled = component.answerId.every((answerId) =>
            disabledList.includes(answerId)
          );

          // Check if ONLY correct answers are disabled (no incorrect answers)
          const onlyCorrectDisabled = disabledList.every(
            (disabledId) => component.answerId?.includes(disabledId) ?? false
          );

          isActivityCompleted = allCorrectDisabled && onlyCorrectDisabled;
        }

        const isqComponent = isq && isq[componentId];
        // Check if isq component is completed
        if (isqComponent?.correctness) {
          isActivityCompleted = isqComponent.correctness.every((item) =>
            Boolean(Object.values(item)[0])
          );
        }

        // Check if oeq component is completed
        if (oeq && oeq[componentId] && oeq[componentId].complete) {
          isActivityCompleted = true;
        }

        // If the activity is completed, update the menu item progress
        if (isActivityCompleted) {
          updatedMenuList = updatedMenuList.map((item) => {
            if (item.value === ownProps.currentMenu.value) {
              if ((item.maxProgress ?? 0) > (item.currentProgress ?? 0)) {
                ownProps.updateProgress({
                  menu: ownProps.currentMenu.value,
                  menuProgress: (item.currentProgress ?? 0) + 1,
                  overallProgress: ownProps.currentProgress + 1,
                });

                // This is a side effect that directly modifies the component passed in,
                // which is not ideal in a pure function but preserved for compatibility
                component.isComplete = true;

                return {
                  ...item,
                  currentProgress: (item.currentProgress ?? 0) + 1,
                };
              }
            }
            return item;
          });
        }
      });
  }

  // Process menu item dependencies (gating logic)
  const { generatedVariables } = notebook;
  if (updatedMenuList && updatedMenuList.length) {
    const dependantMenuItems = (
      updatedMenuList as MenuItemWithDependencies[]
    ).filter((i) => Array.isArray(i.dependencies) && i.dependencies.length > 0);

    const nextMenu = updatedMenuList.map((i) => ({ ...i }));
    const eq = (a: string, b: string) =>
      typeof a === "string" && typeof b === "string"
        ? a.trim() === b.trim()
        : a === b;

    dependantMenuItems.forEach((depItem) => {
      depItem.dependencies?.forEach((depRule) => {
        const satisfied = areDependenciesSatisfied(
          depRule.dependsOn,
          { cscq, mscq, oeq, generatedVariables },
          updatedMenuList as MenuItemWithDependencies[],
          depRule.negativeAlignment,
          depRule.dependencyLogic
        );

        if (satisfied) {
          const idx = nextMenu.findIndex((m) => eq(m.value, depItem.value));
          if (idx !== -1) {
            const before = nextMenu[idx] as MenuItemWithDependencies;
            const after = { ...before };
            if (Array.isArray(depRule.toggleProps)) {
              depRule.toggleProps.forEach((prop) => {
                (after as unknown as Record<string, unknown>)[prop] = !(
                  before as unknown as Record<string, unknown>
                )[prop];
              });
            }
            const changed = depRule.toggleProps?.some(
              (prop) =>
                (before as unknown as Record<string, unknown>)[prop] !==
                (after as unknown as Record<string, unknown>)[prop]
            );
            if (changed) {
              nextMenu[idx] = after;
            }
          }
        }
      });
    });

    updatedMenuList = nextMenu;
  }

  return {
    ...ownProps,
    menuList: updatedMenuList,
    displayProgress,
    usingNewDesignSystem,
  };
}

export const CommonSectionContent = connect(mapStateToProps)(Component);
export * from "./CommonSectionContentComponent";
